import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { loadAuthenticatedMedia, MEDIA_AVAILABLE, MEDIA_ERROR } from '@/api/authenticatedMedia';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';

export const MEDIA_LOADING = 'loading';

/**
 * Own the short-lived browser URLs for a stable list of business-media
 * requests. The caller supplies [{ key, url }]; protected storage identity
 * never crosses this boundary. Optional owner supplies current(), headers()
 * and timeoutMs for a caller-scoped observation; it must have stable identity
 * for that lifetime. A deadline ends observation, not backend execution.
 */
export function useAuthenticatedMedia(requests, owner) {
  const revision = useSyncExternalStore(
    subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision,
  );
  // A new identity also distinguishes A-B-A observations with reused request keys.
  const identity = useMemo(() => ({ requests, owner, revision }), [requests, owner, revision]);
  const [observation, setObservation] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const objectUrls = [];
    const timers = [];
    const current = () => !controller.signal.aborted
      && revision === getActiveSessionRevision() && (!owner || owner.current());
    const publish = update => {
      if (!current()) return;
      setObservation(previous => !current() ? previous : ({
        identity,
        media: update(previous?.identity === identity ? previous.media : {}),
      }));
    };
    const enabled = requests.filter((request) => request?.key && request?.url);
    publish(() => Object.fromEntries(enabled.map(({ key }) => [key, { state: MEDIA_LOADING }])));

    enabled.forEach(async ({ key, url }) => {
      if (!current()) return;
      const child = new AbortController();
      const abort = () => child.abort();
      controller.signal.addEventListener('abort', abort, { once: true });
      let expired = false;
      const timer = owner ? setTimeout(() => {
        expired = true;
        publish(values => ({ ...values, [key]: { state: MEDIA_ERROR } }));
        child.abort();
      }, owner.timeoutMs) : null;
      if (timer !== null) timers.push(timer);
      try {
        const result = await loadAuthenticatedMedia(url, {
          signal: child.signal, ...(owner ? { headers: owner.headers() } : {}),
        });
        if (!current() || expired) return;
        let next = result;
        if (result.state === MEDIA_AVAILABLE && result.blob) {
          const objectUrl = URL.createObjectURL(result.blob);
          objectUrls.push(objectUrl);
          // Borrowed immutable bytes, not another cache/download. A published
          // viewer must create and dispose its own URL rather than keep ours.
          next = { state: result.state, url: objectUrl, blob: result.blob, contentType: result.contentType };
        }
        publish(values => ({ ...values, [key]: next }));
      } catch (error) {
        if (!current() || expired || error?.name === 'AbortError') return;
        publish(values => ({ ...values, [key]: { state: MEDIA_ERROR } }));
      } finally {
        if (timer !== null) clearTimeout(timer);
        controller.signal.removeEventListener('abort', abort);
      }
    });

    return () => {
      controller.abort();
      timers.forEach(clearTimeout);
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [requests, owner, revision, identity]);

  // Render-time admission must not wait for passive abort/revoke cleanup.
  if (observation?.identity !== identity || revision !== getActiveSessionRevision()
      || (owner && !owner.current())) return {};
  return observation?.media || {};
}
